/* ==========================================================================
   Predictive search (Shopify Predictive Search API + Section Rendering)
   ========================================================================== */

(() => {
  const theme = window.theme || (window.theme = {});

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true' || !theme.routes || !theme.routes.predictiveSearch) return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.form = this.closest('form');

      this.input.addEventListener(
        'input',
        theme.debounce(() => this.onInput(), 250)
      );
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.innerHTML.trim()) this.open();
      });
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          const selected = this.results.querySelector('[aria-selected="true"] a');
          if (selected) {
            event.preventDefault();
            selected.click();
          }
        });
      }
      this.onOutsideClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onOutsideClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutsideClick);
      if (this.controller) this.controller.abort();
    }

    async onInput() {
      const query = this.input.value.trim();
      if (!query) {
        this.close();
        return;
      }
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = theme.strings.searchLoading || '';

      const params = new URLSearchParams({
        q: query,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
      });

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = theme.parseHTML(await response.text());
        const section = doc.querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';
        this.cache.set(query, markup);
        if (this.input.value.trim() === query) this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[search] predictive search failed', error);
        this.close();
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.open();
      const wrapper = this.results.querySelector('[data-results-count]');
      const count = wrapper ? parseInt(wrapper.dataset.resultsCount, 10) || 0 : 0;
      if (this.status) this.status.textContent = theme.format(theme.strings.searchResults, { number: count });
    }

    options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.options().forEach((option) => option.setAttribute('aria-selected', 'false'));
    }

    onKeydown(event) {
      if (this.results.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const options = this.options();
      if (!options.length) return;
      const currentIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      let next = event.key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
      const active = options[next];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
